import { useState, useMemo } from "react";
import { Check, Ban } from "lucide-react";
import type { AdminReport } from "../../data";
import { useAdminData } from "../../hooks/useAdminData";
import { reports as reportsApi } from "../../api/endpoints";
import { AddToast } from "../../components/feedback/Toast";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { FilterChips } from "../../components/ui/FilterChips";
import { PageHeader } from "../../components/ui/PageHeader";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { F, T } from "../../theme/tokens";

// ── Moderation ────────────────────────────────────────────────────────────────

export function ModerationView({ addToast }: { addToast: AddToast }) {
  const { ADMIN_REPORTS, refetchReports, refetchTalents, refetchOrgs } = useAdminData();
  const [statusFilter, setStatusFilter] = useState("All");

  const filtered = useMemo(() => ADMIN_REPORTS.filter((r) => statusFilter === "All" || r.status === statusFilter), [ADMIN_REPORTS, statusFilter]);

  function resolveReport(id: string) {
    reportsApi.update(id, { status: "Resolved" }).then(() => { refetchReports(); addToast("Report resolved", "success"); }).catch(() => addToast("Failed to resolve report", "error"));
  }
  function dismissReport(id: string) {
    reportsApi.update(id, { status: "Dismissed" }).then(() => { refetchReports(); addToast("Dismissed", "info"); }).catch(() => addToast("Failed to dismiss report", "error"));
  }

  // Enforcement reaches the user: a warning is a real in-app notification, a
  // suspension blocks their next request and signs them out.
  function enforceReport(r: AdminReport) {
    const choice = window.prompt(
      `Enforce against ${r.reportedEntityName}?

Type "warn" to send a warning, or "suspend" to block the account.`,
      "warn"
    );
    if (choice === null) return;

    const action = choice.trim().toLowerCase();
    if (action !== "warn" && action !== "suspend") {
      addToast('Type "warn" or "suspend"', "info");
      return;
    }

    const note = window.prompt("Reason (the user sees this):", `Reported for ${r.reason}`);
    if (note === null) return;

    reportsApi.enforce(r.id, action, note)
      .then(() => { refetchReports(); refetchTalents(); refetchOrgs(); addToast(action === "suspend" ? `${r.reportedEntityName} suspended` : "Warning sent", "error"); })
      .catch((e) => addToast(e instanceof Error ? e.message : "Enforcement failed", "error"));
  }

  return (
    <div>
      <PageHeader title="Moderation" sub="Reports and content moderation" />
      <div style={{ display: "flex", gap: "10px", marginBottom: "18px" }}>
        <FilterChips options={["All", "Open", "Under Review", "Resolved", "Dismissed"]} active={statusFilter} onChange={setStatusFilter} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {filtered.map((r) => (
          <Card key={r.id} style={{ padding: "18px 22px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "20px", flexWrap: "wrap" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px", flexWrap: "wrap" }}>
                  <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F }}>{r.reportedEntityName}</p>
                  <StatusBadge status={r.severity} />
                  <StatusBadge status={r.status} />
                </div>
                <p style={{ fontSize: "11px", color: T.sub, fontFamily: F, marginBottom: "6px" }}>
                  {r.reportedEntityType} · {r.reason} · Reported by <span style={{ fontWeight: 600 }}>{r.reportedBy}</span> · {r.submittedDate}
                </p>
                <p style={{ fontSize: "12px", color: T.sub, fontFamily: F, lineHeight: 1.6 }}>{r.description}</p>
                {r.assignedTo && <p style={{ fontSize: "10px", color: T.muted, fontFamily: F, marginTop: "6px" }}>Assigned to {r.assignedTo}</p>}
                {r.resolutionNote && (
                  <p style={{ fontSize: "11px", color: "#166534", background: "#e4f5ec", padding: "6px 10px", borderRadius: "6px", fontFamily: F, marginTop: "8px" }}>Resolution: {r.resolutionNote}</p>
                )}
              </div>
              {r.status !== "Resolved" && r.status !== "Dismissed" && (
                <div className="mod-actions" style={{ display: "flex", gap: "6px", flexShrink: 0, flexWrap: "wrap" }}>
                  <Btn variant="primary" size="xs" onClick={() => resolveReport(r.id)}><Check size={10} /> Resolve</Btn>
                  <Btn variant="ghost" size="xs" onClick={() => dismissReport(r.id)}>Dismiss</Btn>
                  <Btn variant="destructive" size="xs" onClick={() => enforceReport(r)}><Ban size={10} /> Enforce</Btn>
                </div>
              )}
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <Card style={{ padding: "48px", textAlign: "center" }}>
            <p style={{ fontSize: "13px", color: T.muted, fontFamily: F }}>No reports matching this filter.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
